/* ──────────────────────────────────────────────────────────────────
   Product stories (Instagram-style)
   - Bubble previews: muted 3 s loop of the first video (IntersectionObserver)
   - Fullscreen viewer:
     * Ring of pre-decoded <video> layers (current + neighbour stories AND
       neighbour bubbles, prev/next) so any ±1 navigation plays INSTANTLY with
       no loading, no glimpse. A sequence token kills stale swaps (fast nav).
     * Desktop carousel bubble shift (2 peeks/side, arrows follow & fade).
     * Mobile finger-following 3D cube; hold = pause; drag = pause then resume.
   Init pattern mirrors assets/custom_ugc-slider.js (designMode + section load).
   ────────────────────────────────────────────────────────────────── */
(function () {
  var PREVIEW_LOOP_SECONDS = 3;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  function initAll(scope) {
    var rails = (scope || document).querySelectorAll(".product-stories");
    Array.prototype.forEach.call(rails, initRail);
  }

  /* ── Bubble previews ───────────────────────────────────────────── */
  function playPreview(video) {
    if (!video.src && video.dataset.previewSrc) video.src = video.dataset.previewSrc;
    video.muted = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
    video.setAttribute("data-playing", "");
  }

  function setupPreviews(bubbles) {
    var io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                var video = entry.target.querySelector(".product-stories__preview");
                if (!video) return;
                if (entry.isIntersecting) {
                  playPreview(video);
                } else {
                  video.pause();
                  video.removeAttribute("data-playing");
                }
              });
            },
            { threshold: 0.25 }
          )
        : null;

    bubbles.forEach(function (bubble) {
      var video = bubble.querySelector(".product-stories__preview");
      if (!video) return;
      video.addEventListener("timeupdate", function () {
        if (video.currentTime >= PREVIEW_LOOP_SECONDS) video.currentTime = 0;
      });
      if (io) io.observe(bubble);
      else playPreview(video);
    });
  }

  /* ── Rail + viewer ─────────────────────────────────────────────── */
  function initRail(rail) {
    if (rail.dataset.storiesInit === "true") return;
    rail.dataset.storiesInit = "true";

    var bubbles = Array.prototype.slice.call(
      rail.querySelectorAll(".product-stories__bubble")
    );
    if (!bubbles.length) return;

    var viewer = rail.nextElementSibling;
    while (viewer && !viewer.classList.contains("product-stories-viewer")) {
      viewer = viewer.nextElementSibling;
    }
    if (!viewer) return;

    var data = bubbles.map(function (bubble) {
      var script = bubble.querySelector(".product-stories__data");
      var parsed = { title: "", avatar: "", videos: [] };
      if (script) {
        try {
          parsed = JSON.parse(script.textContent);
        } catch (e) {}
      }
      return parsed;
    });

    setupPreviews(bubbles);

    var overlayEl = viewer.querySelector(".product-stories-viewer__overlay");
    var elStage = viewer.querySelector("[data-stage]");
    var elHeader = viewer.querySelector(".product-stories-viewer__header");
    var speedBtn = viewer.querySelector("[data-speed]");
    var speedLabel = viewer.querySelector("[data-speed-label]");
    var layers = Array.prototype.slice.call(viewer.querySelectorAll("[data-video]"));
    var elProgress = viewer.querySelector("[data-progress]");
    var elTitle = viewer.querySelector("[data-viewer-title]");
    var elAvatar = viewer.querySelector("[data-viewer-avatar]");
    var elCover = viewer.querySelector("[data-stage-cover]");
    var navPrev = viewer.querySelector('[data-nav="prev"]');
    var navNext = viewer.querySelector('[data-nav="next"]');
    var peekPrev1 = viewer.querySelector('[data-peek="prev-1"]');
    var peekPrev2 = viewer.querySelector('[data-peek="prev-2"]');
    var peekNext1 = viewer.querySelector('[data-peek="next-1"]');
    var peekNext2 = viewer.querySelector('[data-peek="next-2"]');
    var cubeFace = viewer.querySelector("[data-cube-face]");
    var cubeImg = cubeFace ? cubeFace.querySelector("[data-cube-img]") : null;
    var cubeVideo = cubeFace ? cubeFace.querySelector("[data-cube-video]") : null;

    // Hidden preloaders warm the 2-away bubble first stories (far peeks).
    var preloaders = [];
    for (var pi = 0; pi < 2; pi++) {
      var pv = document.createElement("video");
      pv.muted = true;
      pv.playsInline = true;
      pv.preload = "auto";
      pv.setAttribute("aria-hidden", "true");
      pv.style.cssText = "position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;";
      viewer.appendChild(pv);
      preloaders.push(pv);
    }

    // Poster slider for the OUTGOING main bubble during the desktop carousel.
    // Everything in the carousel moves as a preview image (aperçu): the old main
    // slides out showing its own poster — which is exactly what the new "prev"
    // peek will show — so the centre→side hand-off is poster→identical-poster
    // (no flash, no video frame ever appears on a side bubble). The INCOMING
    // bubble is the real peek sliding to centre (it already shows the right
    // poster), so no image is ever reassigned while visible.
    var outImg = document.createElement("img");
    outImg.alt = "";
    outImg.setAttribute("aria-hidden", "true");
    outImg.style.cssText =
      "position:fixed;display:none;pointer-events:none;z-index:5;object-fit:cover;" +
      "background:#000;border-radius:var(--rounded-block,12px);";
    viewer.appendChild(outImg);
    function placeBox(el, r) {
      el.style.display = "block";
      el.style.left = r.left + "px";
      el.style.top = r.top + "px";
      el.style.width = r.width + "px";
      el.style.height = r.height + "px";
      el.style.transform = "";
      el.style.opacity = "1";
    }

    var activeVid = layers[0];
    var bubbleIndex = 0;
    var storyIndex = 0;
    var paused = false;
    var gesturePaused = false;
    var isMuted = false;
    var speedSteps = [1, 1.5, 2];
    var speedIndex = 0;
    var isAnimatingBubble = false;
    var presentSeq = 0;
    var afterSwapOnce = null;
    var arrowClones = [];
    var originalParent = viewer.parentNode;
    var originalNext = viewer.nextSibling;
    var prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* ── Data helpers ─────────────────────────────────────────────── */
    function vids(bi) {
      return (data[bi] && data[bi].videos) || [];
    }
    function srcAt(bi, si) {
      var v = vids(bi);
      return (v[si] && v[si].src) || "";
    }
    function firstPoster(bi) {
      var b = data[bi];
      if (!b) return "";
      if (b.cover) return b.cover;
      if (b.videos && b.videos[0] && b.videos[0].poster) return b.videos[0].poster;
      return b.avatar || "";
    }
    function coverFor(bi, si) {
      var b = data[bi];
      if (!b) return "";
      var v = (b.videos || [])[si];
      if (v && v.poster) return v.poster;
      return b.cover || b.avatar || "";
    }
    function showStageCover(url) {
      if (!elCover || !url) return;
      if (elCover.getAttribute("src") !== url) elCover.setAttribute("src", url);
      elCover.hidden = false;
    }
    function hideStageCover() {
      if (elCover) elCover.hidden = true;
    }
    function nextStorySrc() {
      return srcAt(bubbleIndex, storyIndex + 1);
    }
    function prevStorySrc() {
      return storyIndex > 0 ? srcAt(bubbleIndex, storyIndex - 1) : "";
    }

    /* ── Pre-decoded layer ring ───────────────────────────────────── */
    // The wanted srcs are kept decoded across the 5 layers so any neighbour
    // (prev/next story, prev/next bubble) plays instantly.
    function wantedSrcs() {
      var w = [srcAt(bubbleIndex, storyIndex)];
      [nextStorySrc(), prevStorySrc(), srcAt(bubbleIndex + 1, 0), srcAt(bubbleIndex - 1, 0)].forEach(
        function (s) {
          if (s && w.indexOf(s) < 0) w.push(s);
        }
      );
      return w;
    }

    function assignLayers() {
      var wants = wantedSrcs();
      var cur = wants[0];
      var free = layers.slice();
      var map = {};
      // Keep layers that already hold a wanted src (no reload).
      wants.forEach(function (s) {
        if (map[s]) return;
        for (var i = 0; i < free.length; i++) {
          if (free[i].getAttribute("src") === s) {
            map[s] = free[i];
            free.splice(i, 1);
            break;
          }
        }
      });
      // Assign the rest to spare layers (these begin buffering now).
      wants.forEach(function (s) {
        if (map[s]) return;
        var el = free.shift();
        if (!el) return;
        el.removeAttribute("poster");
        el.src = s;
        try {
          el.load();
        } catch (e) {}
        map[s] = el;
      });
      layers.forEach(function (l) {
        l.muted = isMuted;
      });
      return map[cur] || layers[0];
    }

    function warmNeighbors() {
      [srcAt(bubbleIndex + 2, 0), srcAt(bubbleIndex - 2, 0)].forEach(function (s, i) {
        var pl = preloaders[i];
        if (pl && s && pl.getAttribute("src") !== s) pl.src = s;
      });
    }

    // Show a layer as active. Instant when it already has a frame (the common
    // case thanks to the ring); otherwise wait for one. Seq-guarded so a fast
    // follow-up navigation can't be overwritten by a stale, slow swap.
    function showActive(el) {
      presentSeq++;
      var seq = presentSeq;
      el.muted = isMuted;
      // Neighbour layers are kept rewound to 0, so the incoming layer is
      // already at the start — no seek (which would flash) before showing it.
      if (el.currentTime > 0.05) {
        try {
          el.currentTime = 0;
        } catch (e) {}
      }
      function reveal() {
        if (seq !== presentSeq) return;
        for (var i = 0; i < layers.length; i++) {
          if (layers[i] !== el) {
            layers[i].classList.remove("is-active");
            try {
              layers[i].pause();
              layers[i].currentTime = 0; // rewind so it's instant when shown next
            } catch (e) {}
          }
        }
        el.classList.add("is-active");
        activeVid = el;
        applyRate(el); // charger une source remet playbackRate à 1 → on ré-applique
        hideStageCover();
        if (!paused && !gesturePaused) {
          var p = el.play();
          if (p && p.catch) p.catch(function () {});
        }
        // Drop the carousel peek / cube face only once a REAL video frame is on
        // screen. requestVideoFrameCallback fires exactly when a new frame is
        // presented, so there's never a blank/poster flash; rAF is the fallback.
        if (afterSwapOnce) {
          var cb = afterSwapOnce;
          afterSwapOnce = null;
          var fired = false;
          var fire = function () {
            if (fired) return;
            fired = true;
            cb();
          };
          if (el.requestVideoFrameCallback) {
            try {
              el.requestVideoFrameCallback(function () {
                requestAnimationFrame(fire);
              });
            } catch (e) {
              requestAnimationFrame(function () {
                requestAnimationFrame(fire);
              });
            }
          } else {
            requestAnimationFrame(function () {
              requestAnimationFrame(fire);
            });
          }
          // Safety: if no frame is ever presented (e.g. paused), reveal anyway.
          setTimeout(fire, 250);
        }
      }
      if (el.readyState >= 2) {
        reveal();
      } else {
        var on = function () {
          el.removeEventListener("loadeddata", on);
          el.removeEventListener("canplay", on);
          reveal();
        };
        el.addEventListener("loadeddata", on);
        el.addEventListener("canplay", on);
        setTimeout(function () {
          if (seq === presentSeq && activeVid !== el) reveal();
        }, 1200);
      }
    }

    /* ── Progress bars ────────────────────────────────────────────── */
    function buildBars(count, active) {
      elProgress.innerHTML = "";
      for (var i = 0; i < count; i++) {
        var bar = document.createElement("div");
        bar.className = "product-stories-viewer__bar";
        var fill = document.createElement("span");
        fill.className = "product-stories-viewer__bar-fill";
        fill.style.setProperty("--progress", i < active ? "100%" : "0%");
        bar.appendChild(fill);
        elProgress.appendChild(bar);
      }
    }
    function setBarProgress(idx, pct) {
      var bar = elProgress.children[idx];
      if (bar && bar.firstChild) bar.firstChild.style.setProperty("--progress", pct + "%");
    }

    /* ── Peeks / arrows ───────────────────────────────────────────── */
    function setPeek(peekEl, idx) {
      if (!peekEl) return;
      if (idx >= 0 && idx < data.length) {
        peekEl.hidden = false;
        var img = peekEl.querySelector("[data-peek-img]");
        if (img) img.src = firstPoster(idx);
      } else {
        peekEl.hidden = true;
      }
    }
    function updateNav() {
      // Les flèches passent la story (et enchaînent sur la bulle voisine) : on les
      // masque seulement aux extrémités absolues (1re story de la 1re bulle /
      // dernière story de la dernière bulle), sinon elles restent dispo même
      // pour une bulle unique à plusieurs stories.
      var navVideos = (data[bubbleIndex] && data[bubbleIndex].videos) || [];
      navPrev.hidden = bubbleIndex <= 0 && storyIndex <= 0;
      navNext.hidden = bubbleIndex >= data.length - 1 && storyIndex >= navVideos.length - 1;
      setPeek(peekPrev1, bubbleIndex - 1);
      setPeek(peekPrev2, bubbleIndex - 2);
      setPeek(peekNext1, bubbleIndex + 1);
      setPeek(peekNext2, bubbleIndex + 2);
    }

    /* ── Load a story ─────────────────────────────────────────────── */
    function loadStory() {
      var bubble = data[bubbleIndex];
      if (!bubble) return;
      var videos = bubble.videos || [];
      var story = videos[storyIndex];
      if (!story) {
        nextStory();
        return;
      }
      elTitle.textContent = bubble.title || "";
      elAvatar.innerHTML = bubble.avatar ? '<img src="' + bubble.avatar + '" alt="">' : "";
      buildBars(videos.length, storyIndex);
      setBarProgress(storyIndex, 0);
      paused = false;
      gesturePaused = false;
      viewer.removeAttribute("data-paused");
      layers.forEach(function (l) {
        l.classList.remove("is-active");
      });
      var coverUrl = coverFor(bubbleIndex, storyIndex);
      if (elCover.hidden || !elCover.getAttribute("src")) showStageCover(coverUrl);
      var curEl = assignLayers();
      // During a bubble animation the peeks are mid-slide; reassigning their
      // images here would flash a wrong poster. The carousel/cube reveal calls
      // updateNav() itself, once everything has snapped back into place.
      if (!isAnimatingBubble) updateNav();
      showActive(curEl);
      warmNeighbors();
    }

    function loadBubble(target) {
      bubbleIndex = target;
      storyIndex = 0;
      loadStory();
    }

    /* ── Story navigation (within a bubble, then across) ──────────── */
    function nextStory() {
      var videos = (data[bubbleIndex] && data[bubbleIndex].videos) || [];
      if (storyIndex < videos.length - 1) {
        storyIndex++;
        loadStory();
      } else if (bubbleIndex < data.length - 1) {
        goToBubble(bubbleIndex + 1);
      } else {
        closeViewer();
      }
    }
    function prevStory() {
      if (storyIndex > 0) {
        storyIndex--;
        loadStory();
      } else if (bubbleIndex > 0) {
        goToBubble(bubbleIndex - 1);
      } else {
        try {
          activeVid.currentTime = 0;
        } catch (e) {}
      }
    }

    /* ── Desktop carousel ─────────────────────────────────────────── */
    function moveEl(el, from, to, fadeOut, dur, easing, anims) {
      if (!el || !from || !to) return null;
      var dx = to.left + to.width / 2 - (from.left + from.width / 2);
      var dy = to.top + to.height / 2 - (from.top + from.height / 2);
      var sc = to.width / from.width;
      var end = { transform: "translate(" + dx + "px," + dy + "px) scale(" + sc + ")" };
      if (fadeOut) end.opacity = 0;
      el.style.zIndex = "3";
      var a = el.animate([{ transform: "none" }, end], { duration: dur, easing: easing, fill: "forwards" });
      anims.push(a);
      return a;
    }
    function animateCarousel(target, dir, offset) {
      function rectOf(el) {
        return el && !el.hidden ? el.getBoundingClientRect() : null;
      }
      var slots = [peekPrev2, peekPrev1, elStage, peekNext1, peekNext2]; // pos -2..+2
      var rects = slots.map(rectOf);
      var stageRect = rects[2];
      if (!stageRect) {
        loadBubble(target);
        return;
      }
      var refPeek = rects[1] || rects[3] || rects[0] || rects[4] || null;
      var peekW = refPeek ? refPeek.width : stageRect.width * 0.3;
      var peekH = refPeek ? refPeek.height : stageRect.height * 0.3;
      var stageCx = stageRect.left + stageRect.width / 2;
      var cy = stageRect.top + stageRect.height / 2;
      var stageToPeek = stageRect.width / 2 + 24 + peekW / 2;
      if (rects[3]) stageToPeek = rects[3].left + rects[3].width / 2 - stageCx;
      else if (rects[1]) stageToPeek = stageCx - (rects[1].left + rects[1].width / 2);
      var pitch = peekW + 20;
      if (rects[3] && rects[4]) pitch = rects[4].left + rects[4].width / 2 - (rects[3].left + rects[3].width / 2);
      else if (rects[0] && rects[1]) pitch = rects[1].left + rects[1].width / 2 - (rects[0].left + rects[0].width / 2);
      function slotRect(idx) {
        var isStage = idx === 2;
        var w = isStage ? stageRect.width : peekW;
        var h = isStage ? stageRect.height : peekH;
        var cx;
        if (idx === 2) cx = stageCx;
        else if (idx > 2) cx = stageCx + stageToPeek + (idx - 3) * pitch;
        else cx = stageCx - stageToPeek - (1 - idx) * pitch;
        return { left: cx - w / 2, top: cy - h / 2, width: w, height: h };
      }

      var step = dir === "next" ? -offset : offset;

      isAnimatingBubble = true;
      viewer.classList.add("is-animating-bubble");
      var dur = 440;
      var easing = "cubic-bezier(0.65, 0.05, 0.36, 1)";
      var anims = [];

      // Measure the EXACT positions every slot will rest at AFTER the change, so
      // the slide ends precisely on the final layout (no snap, no gap change at the
      // end). Slot positions are fixed by CSS — only visibility/content changes
      // (incl. the arrows, which affect spacing) — so we briefly apply the target
      // state, measure, then revert. Synchronous: nothing is painted in between.
      var savedIndex = bubbleIndex;
      bubbleIndex = target;
      updateNav();
      var lastRects = slots.map(function (el) {
        if (!el || el.hidden) return null;
        var r = el.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
      });
      bubbleIndex = savedIndex;
      updateNav();
      function destRect(idx) {
        return (idx >= 0 && idx <= 4 && lastRects[idx]) || rects[idx] || slotRect(idx);
      }

      // The whole carousel moves as preview images (aperçu) — no video frame ever
      // appears on a side bubble. The live stage is hidden; the OUTGOING main
      // slides out as its own poster (outImg, = exactly what the new prev peek
      // will show, so that hand-off is poster→identical-poster). The INCOMING peek
      // slides to centre keeping its poster. The stage (video) is revealed only
      // once the slide settles.
      var peekO = parseFloat(getComputedStyle(peekNext1 || peekPrev1 || elStage).getPropertyValue("--peek-o")) || 0.55;

      elStage.style.opacity = "0";
      var outPoster = firstPoster(bubbleIndex);
      if (outPoster) {
        outImg.src = outPoster;
        placeBox(outImg, stageRect);
        // Outgoing main: full opacity → peek opacity as it shrinks to a side slot.
        outImg.style.opacity = "1";
        outImg.style.zIndex = "5";
        anims.push(outImg.animate(
          [{ opacity: 1 }, { opacity: peekO }],
          { duration: dur, easing: easing, fill: "forwards" }
        ));
        moveEl(outImg, stageRect, destRect(2 + step), false, dur, easing, anims);
      }

      // Incoming peek = the one that slides to centre.
      var incomingPeek = slots[2 - step] || null;

      // Side peeks (incl. the incoming one) shift one slot.
      // Stage is skipped — outImg carries the outgoing centre.
      for (var k = 0; k < slots.length; k++) {
        if (!slots[k] || !rects[k] || slots[k] === elStage) continue;
        var destIdx = k + step;
        var fadingOut = destIdx < 0 || destIdx > 4;
        moveEl(slots[k], rects[k], destRect(destIdx), fadingOut, dur, easing, anims);
        // Incoming peek: peek opacity → full opacity as it grows to centre.
        if (!fadingOut && slots[k] === incomingPeek) {
          anims.push(slots[k].animate(
            [{ opacity: peekO }, { opacity: 1 }],
            { duration: dur, easing: easing, fill: "forwards" }
          ));
        }
      }

      // ── Arrows attached to their bubble ───────────────────────────────
      // "next": the main's RIGHT arrow travels to where the old LEFT arrow was and
      // stays (it becomes the new left arrow); the old left arrow drifts out to the
      // side bubble and fades; a new right arrow fades in from the right side
      // bubble. Mirrored for "prev". Transient clones — nothing teleports.
      var lastIdx = data.length - 1;
      function arrowCx(el) {
        var r = el.getBoundingClientRect();
        return r.left + r.width / 2;
      }
      var armDist;
      if (navPrev && !navPrev.hidden) armDist = Math.abs(stageCx - arrowCx(navPrev));
      else if (navNext && !navNext.hidden) armDist = Math.abs(arrowCx(navNext) - stageCx);
      else armDist = stageRect.width / 2 + 40;
      var Lcx = stageCx - armDist;
      var Rcx = stageCx + armDist;
      var sideL = slotRect(1).left + peekW / 2; // left side-bubble centre
      var sideR = slotRect(3).left + peekW / 2; // right side-bubble centre
      function spawnArrow(srcEl, startCx, endCx, fadeIn, fadeOut, morphTo) {
        if (!srcEl) return;
        var c = srcEl.cloneNode(true);
        c.removeAttribute("data-nav");
        c.removeAttribute("hidden");
        c.style.position = "fixed";
        c.style.margin = "0";
        c.style.left = startCx - 22 + "px";
        c.style.top = cy - 22 + "px";
        c.style.zIndex = "6";
        c.style.pointerEvents = "none";
        c.style.opacity = ""; // the real arrow was hidden (opacity 0) before cloning — clear it so the travelling clone is visible
        viewer.appendChild(c);
        arrowClones.push(c);
        var from = { transform: "translateX(0)" };
        var to = { transform: "translateX(" + (endCx - startCx) + "px)" };
        if (fadeIn) { from.opacity = 0; to.opacity = 1; }
        if (fadeOut) { from.opacity = 1; to.opacity = 0; }
        anims.push(c.animate([from, to], { duration: dur, easing: easing, fill: "forwards" }));
        // When the arrow swaps sides, crossfade its chevron (› ↔ ‹) so the symbol
        // transitions smoothly instead of flipping at the end of the travel.
        var baseSvg = c.querySelector("svg");
        var morphSvg = morphTo && morphTo.querySelector("svg");
        if (baseSvg && morphSvg) {
          var overlay = morphSvg.cloneNode(true);
          [baseSvg, overlay].forEach(function (s) {
            s.style.position = "absolute";
            s.style.left = "50%";
            s.style.top = "50%";
            s.style.transform = "translate(-50%,-50%)";
          });
          overlay.style.opacity = "0";
          c.appendChild(overlay);
          anims.push(baseSvg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: dur, easing: easing, fill: "forwards" }));
          anims.push(overlay.animate([{ opacity: 0 }, { opacity: 1 }], { duration: dur, easing: easing, fill: "forwards" }));
        }
      }
      // Hide the real arrows during transit; they reappear at their rest slots
      // (= the clones' end positions) on reveal, so the hand-off is seamless.
      if (navPrev) navPrev.style.opacity = "0";
      if (navNext) navNext.style.opacity = "0";
      var hadLeft = bubbleIndex > 0, hadRight = bubbleIndex < lastIdx;
      var newLeft = target > 0, newRight = target < lastIdx;
      if (dir === "next") {
        if (newLeft) spawnArrow(navNext, Rcx, Lcx, false, false, navPrev); // right arrow travels to old-left slot, chevron morphs › → ‹
        if (hadLeft) spawnArrow(navPrev, Lcx, sideL, false, true); // old left arrow drifts out, fades
        if (newRight) spawnArrow(navNext, sideR, Rcx, true, false); // new right arrow fades in
      } else {
        if (newRight) spawnArrow(navPrev, Lcx, Rcx, false, false, navNext); // left arrow travels to old-right slot, chevron morphs ‹ → ›
        if (hadRight) spawnArrow(navNext, Rcx, sideR, false, true); // old right arrow drifts out, fades
        if (newLeft) spawnArrow(navPrev, sideL, Lcx, true, false); // new left arrow fades in
      }

      finishAfter(anims, dur, target);
    }

    function finishAfter(anims, dur, target) {
      var settled = false;
      function reveal() {
        anims.forEach(function (a) {
          try {
            a.cancel();
          } catch (e) {}
        });
        arrowClones.forEach(function (c) {
          if (c && c.parentNode) c.parentNode.removeChild(c);
        });
        arrowClones.length = 0;
        // Reveal the live stage (already painting a frame behind the incoming
        // peek) and drop the outgoing poster — all in the SAME tick as the peeks
        // snap back and their images are reassigned (transition still off via the
        // is-animating-bubble class), so no side bubble ever flashes a wrong image.
        elStage.style.opacity = "";
        outImg.style.display = "none";
        [peekPrev2, peekPrev1, elStage, peekNext1, peekNext2, navPrev, navNext, elHeader, elProgress].forEach(
          function (el) {
            if (el) {
              el.style.transform = "";
              el.style.zIndex = "";
              el.style.opacity = "";
            }
          }
        );
        updateNav();
        viewer.classList.remove("is-animating-bubble");
        isAnimatingBubble = false;
        [elHeader, elProgress].forEach(function (el) {
          if (el && el.animate) el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: "ease" });
        });
      }
      function finish() {
        if (settled) return;
        settled = true;
        // Stage becomes visible but stays occluded by the incoming peek (which
        // shows the new bubble's poster): the video presents a frame behind it.
        // reveal() then swaps peek → identical live stage once a real frame is up
        // (see showActive). Peek images are NOT reassigned until reveal.
        elStage.style.opacity = "";
        afterSwapOnce = reveal;
        loadBubble(target);
        setTimeout(function () {
          if (afterSwapOnce === reveal) {
            afterSwapOnce = null;
            reveal();
          }
        }, 1000);
      }
      // Trigger on the full-duration slide animation (anims[0] = outgoing poster),
      // not a shorter one, so the row doesn't snap before the slide completes.
      if (anims[0]) anims[0].onfinish = finish;
      setTimeout(finish, dur + 40);
    }

    /* ── Mobile 3D cube ───────────────────────────────────────────── */
    function cubeFront(theta, dir) {
      var w = window.innerWidth;
      var rot = dir === "next" ? -theta : theta;
      return "translateZ(" + -w / 2 + "px) rotateY(" + rot + "deg) translateZ(" + w / 2 + "px)";
    }
    function cubeSide(theta, dir) {
      var w = window.innerWidth;
      var rot = dir === "next" ? -theta : theta;
      var sb = dir === "next" ? 90 : -90;
      return "translateZ(" + -w / 2 + "px) rotateY(" + rot + "deg) rotateY(" + sb + "deg) translateZ(" + w / 2 + "px)";
    }
    function setCubeFace(target, dir) {
      if (!cubeFace) return;
      if (cubeFace.getAttribute("data-for") !== String(target)) {
        if (cubeImg) cubeImg.src = firstPoster(target);
        if (cubeVideo) {
          var vs = srcAt(target, 0);
          if (vs) {
            if (cubeVideo.getAttribute("src") !== vs) cubeVideo.src = vs;
            cubeVideo.muted = true;
            // Hold the cube face on frame 0 (paused) — the same frame the stage
            // layer starts from. When the layer takes over there's no backward
            // jump from a preview frame, so the story plays instantly, seamlessly.
            try {
              cubeVideo.pause();
              cubeVideo.currentTime = 0;
            } catch (e) {}
          }
        }
        cubeFace.setAttribute("data-for", String(target));
      }
      cubeFace.hidden = false;
    }
    function applyCube(theta, dir, target) {
      elStage.style.transition = "none";
      elStage.style.backfaceVisibility = "hidden";
      elStage.style.transform = cubeFront(theta, dir);
      if (target != null && cubeFace) {
        setCubeFace(target, dir);
        cubeFace.style.transition = "none";
        cubeFace.style.transform = cubeSide(theta, dir);
      }
    }
    function clearCube() {
      [elStage, cubeFace].forEach(function (el) {
        if (el && el.getAnimations) {
          el.getAnimations().forEach(function (a) {
            try {
              a.cancel();
            } catch (e) {}
          });
        }
      });
      elStage.style.transition = "";
      elStage.style.transform = "";
      elStage.style.backfaceVisibility = "";
      if (cubeFace) {
        cubeFace.hidden = true;
        cubeFace.style.transition = "";
        cubeFace.style.transform = "";
        cubeFace.removeAttribute("data-for");
        if (cubeVideo) {
          try {
            cubeVideo.pause();
          } catch (e) {}
        }
      }
    }
    function runCube(dir, target, startTheta) {
      if (!cubeFace || !elStage.animate) {
        loadBubble(target);
        clearCube();
        return;
      }
      isAnimatingBubble = true;
      viewer.classList.add("is-animating-bubble");
      setCubeFace(target, dir);
      var dur = 420;
      var easing = "cubic-bezier(0.2, 0.7, 0.2, 1)";
      var aF = elStage.animate(
        [{ transform: cubeFront(startTheta || 0, dir) }, { transform: cubeFront(90, dir) }],
        { duration: dur, easing: easing, fill: "forwards" }
      );
      cubeFace.animate(
        [{ transform: cubeSide(startTheta || 0, dir) }, { transform: cubeSide(90, dir) }],
        { duration: dur, easing: easing, fill: "forwards" }
      );
      var settled = false;
      function reveal() {
        clearCube();
        viewer.classList.remove("is-animating-bubble");
        isAnimatingBubble = false;
        updateNav(); // loadStory skipped it during the animation
      }
      function settle() {
        if (settled) return;
        settled = true;
        loadBubble(target);
        // The cube face and the layer both sit on frame 0, so the hand-off is
        // identical-frame. Reveal after a couple of frames (visibility-independent,
        // since the stage is edge-on here) — no stall, no poster glimpse.
        requestAnimationFrame(function () {
          requestAnimationFrame(reveal);
        });
      }
      aF.onfinish = settle;
      setTimeout(settle, dur + 120);
    }
    function cancelCube(dir, startTheta) {
      if (!elStage.animate) {
        clearCube();
        resumeForGesture();
        return;
      }
      isAnimatingBubble = true;
      var dur = 200;
      var easing = "cubic-bezier(0.4, 0, 0.2, 1)";
      var aF = elStage.animate(
        [{ transform: cubeFront(startTheta || 0, dir) }, { transform: cubeFront(0, dir) }],
        { duration: dur, easing: easing, fill: "forwards" }
      );
      if (cubeFace && !cubeFace.hidden) {
        cubeFace.animate(
          [{ transform: cubeSide(startTheta || 0, dir) }, { transform: cubeSide(0, dir) }],
          { duration: dur, easing: easing, fill: "forwards" }
        );
      }
      var settled = false;
      function settle() {
        if (settled) return;
        settled = true;
        clearCube();
        isAnimatingBubble = false;
        resumeForGesture();
      }
      aF.onfinish = settle;
      setTimeout(settle, dur + 80);
    }

    function goToBubble(target) {
      if (isAnimatingBubble) return;
      target = Math.max(0, Math.min(data.length - 1, target));
      if (target === bubbleIndex) return;
      var dir = target > bubbleIndex ? "next" : "prev";
      var offset = Math.abs(target - bubbleIndex);
      if (prefersReduced || !elStage.animate || offset > 2) {
        loadBubble(target);
        return;
      }
      if (window.matchMedia("(min-width: 768px)").matches) {
        animateCarousel(target, dir, offset);
      } else {
        runCube(dir, target, 0);
      }
    }

    /* ── Controls / gesture pause ─────────────────────────────────── */
    function togglePlay() {
      if (paused) {
        var p = activeVid.play();
        if (p && p.catch) p.catch(function () {});
        paused = false;
        viewer.removeAttribute("data-paused");
      } else {
        activeVid.pause();
        paused = true;
        viewer.setAttribute("data-paused", "");
      }
    }
    function toggleMute() {
      isMuted = !isMuted;
      layers.forEach(function (v) {
        v.muted = isMuted;
      });
      if (isMuted) viewer.setAttribute("data-muted", "");
      else viewer.removeAttribute("data-muted");
    }
    // Applique la vitesse courante en conservant la hauteur de voix (pitch)
    function applyRate(v) {
      try {
        v.preservesPitch = true;
        v.mozPreservesPitch = true;
        v.webkitPreservesPitch = true;
        v.playbackRate = speedSteps[speedIndex];
      } catch (e) {}
    }
    function cycleSpeed() {
      speedIndex = (speedIndex + 1) % speedSteps.length;
      var rate = speedSteps[speedIndex];
      layers.forEach(applyRate);
      if (speedLabel) speedLabel.textContent = rate + "×";
      if (speedBtn) speedBtn.setAttribute("aria-label", "Playback speed " + rate + "x");
    }
    function pauseForGesture() {
      if (paused || gesturePaused) return;
      gesturePaused = true;
      try {
        activeVid.pause();
      } catch (e) {}
    }
    function resumeForGesture() {
      if (!gesturePaused) return;
      gesturePaused = false;
      if (!paused) {
        var p = activeVid.play();
        if (p && p.catch) p.catch(function () {});
      }
    }

    function onKey(e) {
      if (viewer.hidden) return;
      if (e.key === "Escape") closeViewer();
      else if (e.key === "ArrowRight") nextStory();
      else if (e.key === "ArrowLeft") prevStory();
      else if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        togglePlay();
      }
    }

    /* ── Open / close ─────────────────────────────────────────────── */
    function openViewer(bi, si) {
      bubbleIndex = bi;
      storyIndex = si;
      document.body.classList.add("product-stories-open");
      if (viewer.parentNode !== document.body) document.body.appendChild(viewer);
      viewer.hidden = false;
      viewer.setAttribute("aria-hidden", "false");
      viewer.classList.remove("is-closing");
      var instantCover = "";
      var bubblePoster = bubbles[bi] && bubbles[bi].querySelector(".product-stories__poster");
      if (bubblePoster && bubblePoster.getAttribute("src")) instantCover = bubblePoster.getAttribute("src");
      else instantCover = coverFor(bi, si);
      showStageCover(instantCover);
      if (!prefersReduced) {
        viewer.classList.add("is-opening");
        setTimeout(function () {
          viewer.classList.remove("is-opening");
        }, 500);
      }
      loadStory();
      document.addEventListener("keydown", onKey);
    }

    function resetCursor() {
      var mc = document.querySelector("mouse-cursor");
      if (mc) {
        mc.classList.remove("active");
        mc.classList.remove("pressed");
      }
    }

    function finalizeClose() {
      viewer.hidden = true;
      viewer.setAttribute("aria-hidden", "true");
      viewer.classList.remove("is-closing");
      viewer.classList.remove("is-opening");
      if (elStage.getAnimations) {
        elStage.getAnimations().forEach(function (a) {
          try {
            a.cancel();
          } catch (e) {}
        });
      }
      elStage.style.transform = "";
      elStage.style.transition = "";
      elStage.style.borderRadius = "";
      elStage.style.transformOrigin = "";
      elStage.style.opacity = "";
      if (overlayEl) {
        overlayEl.style.opacity = "";
        overlayEl.style.transition = "";
      }
      layers.forEach(function (v) {
        v.pause();
      });
      hideStageCover();
      document.body.classList.remove("product-stories-open");
      document.removeEventListener("keydown", onKey);
      resetCursor();
      if (originalNext && originalNext.parentNode === originalParent) {
        originalParent.insertBefore(viewer, originalNext);
      } else if (originalParent) {
        originalParent.appendChild(viewer);
      }
    }

    function closeViewer() {
      if (viewer.hidden || viewer.classList.contains("is-closing")) return;
      try {
        activeVid.pause();
      } catch (e) {}
      if (prefersReduced) {
        finalizeClose();
        return;
      }
      viewer.classList.remove("is-opening");
      viewer.classList.add("is-closing");
      setTimeout(finalizeClose, 280);
    }

    function dragClose() {
      var h = window.innerHeight;
      var prog = Math.min(drag.dy / (h * 0.6), 1);
      var startT = "translateY(" + drag.dy + "px) scale(" + (1 - prog * 0.2) + ")";
      var endT = "translateY(" + (drag.dy + h * 0.12) + "px) scale(0.45)";
      if (elStage.animate) {
        elStage.style.transition = "";
        elStage.animate(
          [{ transform: startT, opacity: 1 }, { transform: endT, opacity: 0 }],
          { duration: 240, easing: "cubic-bezier(0.65, 0.05, 0.36, 1)", fill: "forwards" }
        );
        if (overlayEl) {
          overlayEl.animate([{ opacity: Number(overlayEl.style.opacity) || 1 }, { opacity: 0 }], {
            duration: 240,
            fill: "forwards",
          });
        }
      }
      setTimeout(finalizeClose, 230);
    }

    /* ── Wiring ───────────────────────────────────────────────────── */
    // Preload every bubble cover / poster so peeks, cube face and the stage cover
    // never flash (blank → image) at the moment content changes.
    data.forEach(function (b, i) {
      var u = b.cover || firstPoster(i);
      if (u) {
        var im = new Image();
        im.src = u;
      }
    });

    bubbles.forEach(function (bubble, i) {
      bubble.addEventListener("click", function () {
        openViewer(i, 0);
      });
    });

    layers.forEach(function (v) {
      v.addEventListener("timeupdate", function () {
        if (v !== activeVid || !v.duration) return;
        setBarProgress(storyIndex, (v.currentTime / v.duration) * 100);
      });
      v.addEventListener("ended", function () {
        if (v === activeVid) nextStory();
      });
    });

    var suppressTap = false;
    var suppressTapTimer = null;
    // Un maintien / slide (même relâché) ne doit jamais compter comme un tap :
    // on consomme le clic de synthèse qui suit, quel que soit son délai.
    function consumeSuppressedTap() {
      if (!suppressTap) return false;
      suppressTap = false;
      clearTimeout(suppressTapTimer);
      return true;
    }
    viewer.querySelector('[data-tap="left"]').addEventListener("click", function () {
      if (consumeSuppressedTap()) return;
      prevStory();
    });
    viewer.querySelector('[data-tap="right"]').addEventListener("click", function () {
      if (consumeSuppressedTap()) return;
      nextStory();
    });

    navPrev.addEventListener("click", prevStory);
    navNext.addEventListener("click", nextStory);

    if (peekPrev1) peekPrev1.addEventListener("click", function () { goToBubble(bubbleIndex - 1); });
    if (peekNext1) peekNext1.addEventListener("click", function () { goToBubble(bubbleIndex + 1); });
    if (peekPrev2) peekPrev2.addEventListener("click", function () { goToBubble(bubbleIndex - 2); });
    if (peekNext2) peekNext2.addEventListener("click", function () { goToBubble(bubbleIndex + 2); });

    viewer.querySelector("[data-playpause]").addEventListener("click", togglePlay);
    viewer.querySelector("[data-mute]").addEventListener("click", toggleMute);
    if (speedBtn) speedBtn.addEventListener("click", cycleSpeed);
    Array.prototype.forEach.call(viewer.querySelectorAll("[data-close]"), function (el) {
      el.addEventListener("click", closeViewer);
    });

    /* ── Touch gestures (mobile) ──────────────────────────────────── */
    var drag = { active: false, axis: null, x0: 0, y0: 0, dx: 0, dy: 0 };
    if (elStage) {
      elStage.addEventListener(
        "touchstart",
        function (e) {
          if (isAnimatingBubble) return;
          drag.active = true;
          drag.axis = null;
          drag.x0 = e.touches[0].clientX;
          drag.y0 = e.touches[0].clientY;
          drag.dx = 0;
          drag.dy = 0;
          drag.theta = 0;
          drag.warmed = false;
          drag.held = false;
          drag.moved = false;
          drag.mobile = window.matchMedia("(max-width: 767px)").matches;
          // Press-and-hold pauses playback (resumes on release).
          if (drag.mobile) {
            drag.holdTimer = setTimeout(function () {
              if (drag.active && drag.axis === null) {
                pauseForGesture();
                drag.held = true;
              }
            }, 200);
          }
        },
        { passive: true }
      );
      elStage.addEventListener(
        "touchmove",
        function (e) {
          if (!drag.active) return;
          drag.dx = e.touches[0].clientX - drag.x0;
          drag.dy = e.touches[0].clientY - drag.y0;
          // Tout déplacement notable (même en dessous du seuil d'axe) marque un
          // slide → le relâchement ne comptera pas comme un tap.
          if (Math.hypot(drag.dx, drag.dy) > 6) drag.moved = true;
          if (drag.axis === null && Math.hypot(drag.dx, drag.dy) > 10) {
            drag.axis = Math.abs(drag.dx) > Math.abs(drag.dy) ? "h" : "v";
            elStage.style.transition = "none";
            clearTimeout(drag.holdTimer);
            // A drag pauses the video (resumes when it snaps back / cancels).
            pauseForGesture();
          }
          if (drag.axis === "v" && drag.dy > 0) {
            var prog = Math.min(drag.dy / (window.innerHeight * 0.6), 1);
            elStage.style.transform = "translateY(" + drag.dy + "px) scale(" + (1 - prog * 0.2) + ")";
            elStage.style.borderRadius = 12 + prog * 24 + "px";
            if (overlayEl) overlayEl.style.opacity = String(1 - prog * 0.6);
          } else if (drag.axis === "h" && drag.mobile) {
            var w = window.innerWidth;
            drag.dir = drag.dx < 0 ? "next" : "prev";
            drag.target = drag.dx < 0 ? bubbleIndex + 1 : bubbleIndex - 1;
            var canGo = drag.target >= 0 && drag.target < data.length;
            var p = Math.min(Math.abs(drag.dx) / w, 1) * (canGo ? 1 : 0.22);
            drag.theta = p * 90;
            applyCube(drag.theta, drag.dir, canGo ? drag.target : null);
          }
        },
        { passive: true }
      );
      elStage.addEventListener(
        "touchend",
        function () {
          if (!drag.active) return;
          drag.active = false;
          clearTimeout(drag.holdTimer);
          if (drag.held || drag.axis !== null || drag.moved) {
            // Consommé par le prochain clic (voir consumeSuppressedTap), avec un
            // filet de sécurité si aucun clic de synthèse ne suit le maintien.
            suppressTap = true;
            clearTimeout(suppressTapTimer);
            suppressTapTimer = setTimeout(function () {
              suppressTap = false;
            }, 400);
          }
          if (drag.axis === "v") {
            if (drag.dy > 110) {
              dragClose();
            } else {
              elStage.style.transition =
                "transform var(--animation-fast), border-radius var(--animation-fast)";
              elStage.style.transform = "";
              elStage.style.borderRadius = "";
              if (overlayEl) {
                overlayEl.style.transition = "opacity var(--animation-fast)";
                overlayEl.style.opacity = "";
              }
              setTimeout(function () {
                elStage.style.transition = "";
                if (overlayEl) overlayEl.style.transition = "";
              }, 300);
              resumeForGesture();
            }
          } else if (drag.axis === "h" && drag.mobile) {
            var w = window.innerWidth;
            var canGo = drag.target >= 0 && drag.target < data.length;
            if (canGo && (Math.abs(drag.dx) > w * 0.22 || drag.theta > 38)) {
              runCube(drag.dir, drag.target, drag.theta);
            } else if (drag.theta > 0) {
              cancelCube(drag.dir, drag.theta);
            } else {
              resumeForGesture();
            }
          } else {
            resumeForGesture();
          }
          drag.axis = null;
          drag.warmed = false;
          drag.theta = 0;
        },
        { passive: true }
      );
    }
  }

  ready(function () {
    initAll(document);
  });

  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
})();
